import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "src/app/hooks";
import { SplashScreen } from "src/components/splash-screen";
import { authSlice, authThunks } from "src/features/auth/authSlice";
import { sendUserEmailToAnalytics } from "src/libs/analytics";
import { useThumbzApi } from "src/services/thumbz-api";
import { createClient } from "src/utils/supabase/client";
import * as Sentry from "@sentry/react";
import type { Session } from "@supabase/supabase-js";

interface IAuthGuardProps {
  children: React.ReactNode;
}

export const AuthGuard: React.FC<IAuthGuardProps> = ({ children }) => {
  const { api } = useThumbzApi();
  const supabase = createClient();
  const loggeduser = useAppSelector(authSlice.selectors.selectUser);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);

  // Prevent duplicate backend login calls (StrictMode/re-mounts)
  const attemptedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      let session: Session | null = null;
      try {
        const { data } = await supabase.auth.getSession();
        if (!data.session) {
          if (!cancelled) setLoading(false);
          return;
        }
        session = data.session;

        // If user already hydrated, skip
        if (loggeduser) {
          if (!cancelled) setLoading(false);
          return;
        }

        // Avoid firing login more than once unless previous attempt failed
        if (attemptedRef.current) {
          if (!cancelled) setLoading(false);
          return;
        }
        attemptedRef.current = true;

        const loginResponse = await api.auth.authControllerLogin({
          firebase_idToken: data.session.user.id,
        });

        if (loginResponse?.user?.usu_name && loginResponse?.user?.usu_external_uid) {
          Sentry.setUser({
            email: data.session.user.email,
            id: loginResponse.user.usu_external_uid,
            username: loginResponse.user.usu_name,
          });
        }

        await dispatch(authThunks.handleAuthToken({ event: "SIGNED_IN", session: data.session }));
        await dispatch(
          authThunks.handleAuthSignIn({
            user: loginResponse.user,
            supabaseUser: data.session.user,
            supabaseSession: data.session,
            favoriteOrganization: loginResponse.favorite_organization,
          }),
        );
      } catch (error) {
        console.error(error);
        // Allow retry if it fails
        attemptedRef.current = false;
      } finally {
        if (!cancelled) setLoading(false);
        console.log("Auth SignIn atualizado");

        const email = session?.user.email;
        if (email) {
          await sendUserEmailToAnalytics(email);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // Intentionally exclude `loggeduser` from deps to avoid re-running post-hydration
  }, [api.auth, dispatch, supabase.auth, loggeduser]);

  // Mostre a SplashScreen enquanto está carregando
  if (loading) {
    return <SplashScreen />;
  }

  return <>{loggeduser ? <>{children}</> : <SplashScreen />}</>;
};
